import Link from "next/link";
import { Community, User } from "@prisma/client";

import { cn } from "~/utils/fontsConfig";
import { FollowButton } from "~/components/v3/follow/FollowButton";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { slugifyUser } from "~/utils/string";

type InfoboxProps = {
    community: Pick<
        Community,
        "name" | "image" | "slug" | "description" | "title" | "id"
    > | null;
    profile: Pick<
        User,
        "name" | "image" | "slug" | "bio" | "id" | "email"
    > | null;
    linkText?: string;
    target?: string;
    className?: string;
    eventSlug?: string;
};

export const Infobox = ({
    community,
    profile,
    linkText,
    target,
    className,
    eventSlug,
}: InfoboxProps) => {
    const description = community
        ? community.description
        : (profile?.bio ??
          "No description provided yet, we'll show it here once the host updates their profile");
    const link = community
        ? `/${community?.slug}`
        : `/profile/${slugifyUser({
              name: profile?.name,
              id: profile?.id,
              slug: profile?.slug,
          })}`;

    return (
        <div className={cn("p-6 md:px-0 md:py-4", className)}>
            <h4>{community?.title ?? ""}</h4>
            <div className="flex gap-4">
                <Link href={link} target={target ?? "_blank"}>
                    {community ? (
                        <CommunityAvatar
                            className="h-14 w-14 md:h-20 md:w-20"
                            community={community}
                        />
                    ) : (
                        <UserAvatar
                            className="h-14 w-14 md:h-20 md:w-20"
                            user={{
                                name: profile?.name,
                                email: profile?.email,
                                image: profile?.image,
                            }}
                        />
                    )}
                </Link>

                <div className="flex flex-col gap-2">
                    <div>{description}</div>
                    <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                        <Link
                            href={link}
                            className="text-v3-primary"
                            target={target}
                        >
                            {linkText !== undefined ? linkText : "Learn more"}
                        </Link>
                        {community && (
                            <FollowButton
                                communityId={community.id}
                                variant="link"
                                variantFollowing="link"
                                className="grow-0 px-0 capitalize text-v3-primary shadow-none disabled:bg-transparent disabled:text-v3-N400"
                                classNameFollowing="text-v3-N400"
                                text={`Follow ${community.name}`}
                                eventSlug={eventSlug}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
